import { BackendApiClient } from './backend-client';
import type { ScopeContext } from '../model/types';
import { backendValidationErrorFrom } from '@/shared/lib/backend-errors';
import { requireList, UNREADABLE_RESPONSE_CODE } from '@/shared/lib/response-shape';
import { BackendValidationError } from '@/shared/lib/backend-errors';
import type { UserStatus, UserType } from './user';

const backendClient = new BackendApiClient();

// b-cell-account caps `limit` at 10, well under the fleet's usual 100, so a listing of any
// size arrives as many small pages. The screen fetches them on demand rather than up front.
const SERVER_PAGE_SIZE = 10;
// Shared by both paths that follow a cursor: the drain below and the screen's
// incremental loader. A budget neither can exceed is what stops a backend that keeps
// handing out fresh cursors without ever growing the list.
export const MAX_PAGES = 50;

export type UserAccessStatus = 'active' | 'canceled';

export interface AccessRole {
  id: string;
  name: string;
  display_name?: string;
  type?: 'default' | 'custom' | 'automatic';
  expiration?: string;
}

export interface UserAccess {
  id: string;
  status: UserAccessStatus;
  roles: AccessRole[];
  scope_id: string;
  scope_type: 'account' | 'holder';
  user_type: UserType;
  user_id: string;
  user_name: string | null;
  user_reference_id: string | null;
  user_status: UserStatus | null;
  // Optional because `requireAccessList` deliberately does not prove it: an account cell
  // predating the field still answers the other two tabs correctly, so its absence is a
  // deploy state rather than a malformed response. Declaring it required would say the
  // opposite of what the validator does.
  created?: string;
  expiration?: string;
}

export interface UserAccessPage {
  accesses: UserAccess[];
  cursor: string;
}

export interface UserAccessFilters {
  status?: UserAccessStatus;
  user_type?: UserType;
  // Inclusive bounds on the access's `created`, and deliberately spelled differently.
  //
  // `after` is a bare `YYYY-MM-DD`. The backend reads one as midnight in America/Sao_Paulo,
  // which is already the right lower edge for "from".
  //
  // `before` is a full instant (`2026-08-13T23:59:59.999-03:00`) because that same reading
  // makes a bare date the *first* instant of its day: sent raw it would exclude the whole
  // day the user named, and an equal after/before pair would select nothing. Do not
  // symmetrise these — `filtersFor` composes the upper bound through
  // `endOfDayInFilterZone`.
  after?: string;
  before?: string;
}

export interface AccessGrant {
  user_id: string;
  role_ids: string[];
}


/**
 * Read the `accesses` array and check the fields the screen dereferences while rendering.
 *
 * `requireList` proves the value is an array and then *casts* its elements, so a row missing
 * `roles` passes the fetch and throws inside the render tree instead -- outside any try, with
 * no error boundary above it, which blanks the whole console rather than one row. The three
 * documented-nullable fields (`user_name`, `user_reference_id`, `user_status`) are excluded
 * deliberately: the render path already tolerates them being null.
 *
 * `status` is checked because its absence does not throw -- it makes the screen assert
 * something false. The access screen reads `status !== 'active'` to mark a row revoked and
 * disable both its actions, so a missing value would brand every row canceled on a tab that
 * had just pinned `status=active` server-side: the screen contradicting the query that
 * fetched it, with nothing on screen to say so.
 *
 * `created` is deliberately *not* checked. It is display-only, and an account cell that
 * predates the field still answers the Persons and Integrations tabs correctly (their
 * `status` and `user_type` filters long precede it) -- so its absence is a real deploy-skew
 * state, and losing the whole listing over a missing date would be a worse answer than an
 * unknown one. It renders as "N/A" rather than as a date.
 */
export function requireAccessList(body: unknown): UserAccess[] {
  const accesses = requireList<UserAccess>(body, 'accesses', 'Unreadable access response');

  const malformed = accesses.some(access =>
    !access
    || typeof access.id !== 'string'
    || typeof access.user_id !== 'string'
    || typeof access.user_type !== 'string'
    || typeof access.status !== 'string'
    || !Array.isArray(access.roles)
  );

  if (malformed) {
    throw new BackendValidationError('Unreadable access response', UNREADABLE_RESPONSE_CODE);
  }

  return accesses;
}

function accessQuery(filters: UserAccessFilters, cursor: string): string {
  const parts = [`limit=${SERVER_PAGE_SIZE}`];
  if (filters.status) parts.push(`status=${filters.status}`);
  if (filters.user_type) parts.push(`user_type=${filters.user_type}`);
  if (filters.after) parts.push(`after=${encodeURIComponent(filters.after)}`);
  if (filters.before) parts.push(`before=${encodeURIComponent(filters.before)}`);
  if (cursor) parts.push(`cursor=${encodeURIComponent(cursor)}`);
  return parts.join('&');
}

export async function getUserAccessPage(
  sessionId: string,
  scopeContext: ScopeContext,
  filters: UserAccessFilters = {},
  cursor = '',
): Promise<UserAccessPage> {
  const response = await backendClient.get(
    `/user-access?${accessQuery(filters, cursor)}`,
    sessionId,
    scopeContext,
  );

  if (!response.success || !response.data) {
    throw response.validationError
      ?? backendValidationErrorFrom({}, response.error || 'Failed to fetch accesses');
  }

  const accesses = requireAccessList(response.data);
  const nextCursor = (response.data as { cursor?: unknown }).cursor;
  return { accesses, cursor: typeof nextCursor === 'string' ? nextCursor : '' };
}

export interface UserAccessListing {
  accesses: UserAccess[];
  cursor: string;
  truncated: boolean;
  // Pages this call consumed, so a caller running both paths against one listing can hold
  // them to a single budget rather than letting each spend MAX_PAGES independently.
  pagesLoaded: number;
}

/**
 * Follow the cursor from `cursor` to the end of the listing.
 *
 * Used where a partial listing would be a wrong answer rather than a slow one — today that
 * is the search box, which may not report "no such member" until the listing is exhausted.
 * `truncated` reports that the page cap was reached with a cursor still outstanding, so the
 * screen can say so instead of presenting a partial list as the whole set, and so the caller
 * has a terminal state to stop on.
 *
 * `shouldContinue` is checked before every page, `budget` included. A caller whose listing
 * was reset mid-drain (a tab click, a scope change, a revoke's refresh) has nothing left on
 * screen that will read further pages, so it passes a check that goes false the moment its
 * generation is no longer current — otherwise an abandoned drain keeps issuing up to
 * `budget` further signed round-trips whose results are discarded on arrival. It defaults to
 * always-true so a caller with no invalidation concept (a direct script, a test) is
 * unaffected.
 */
export async function listRemainingUserAccesses(
  sessionId: string,
  scopeContext: ScopeContext,
  filters: UserAccessFilters = {},
  cursor = '',
  budget = MAX_PAGES,
  shouldContinue: () => boolean = () => true,
): Promise<UserAccessListing> {
  const accesses: UserAccess[] = [];
  let nextCursor = cursor;

  for (let page = 0; page < budget; page++) {
    if (!shouldContinue()) {
      return { accesses, cursor: nextCursor, truncated: true, pagesLoaded: page };
    }
    const result = await getUserAccessPage(sessionId, scopeContext, filters, nextCursor);
    accesses.push(...result.accesses);
    if (!result.cursor) {
      return { accesses, cursor: '', truncated: false, pagesLoaded: page + 1 };
    }
    // The backend handed back the cursor we sent: no progress, and no answer about whether
    // more exists. Stop, and report it as not fully loaded rather than as complete.
    //
    // The cursor is returned, not cleared. `truncated` with no cursor is the one combination
    // no control can act on -- Continue reads the cursor, Load more reads it through
    // `canAutoLoad` -- so clearing it here stranded the listing with a notice saying it was
    // unfinished and nothing on screen able to finish it. Keeping it collapses this into the
    // budget case below, which already has a working exit. Asking again may stall again;
    // that is the honest offer when the backend has told us nothing.
    if (result.cursor === nextCursor) {
      return { accesses, cursor: nextCursor, truncated: true, pagesLoaded: page + 1 };
    }
    nextCursor = result.cursor;
  }

  return { accesses, cursor: nextCursor, truncated: true, pagesLoaded: budget };
}

export async function grantUserAccesses(
  sessionId: string,
  scopeContext: ScopeContext,
  grants: AccessGrant[],
): Promise<UserAccess[]> {
  const response = await backendClient.post(
    '/user-access',
    sessionId,
    { accesses: grants },
    scopeContext,
  );

  if (!response.success || !response.data) {
    throw response.validationError
      ?? backendValidationErrorFrom({}, response.error || 'Failed to grant access');
  }

  return requireAccessList(response.data);
}

export async function cancelUserAccesses(
  sessionId: string,
  scopeContext: ScopeContext,
  accessIds: string[],
): Promise<UserAccess[]> {
  const response = await backendClient.patch(
    '/user-access',
    sessionId,
    { accesses: accessIds.map(id => ({ id, status: 'canceled' })) },
    scopeContext,
  );

  if (!response.success || !response.data) {
    throw response.validationError
      ?? backendValidationErrorFrom({}, response.error || 'Failed to cancel access');
  }

  return requireAccessList(response.data);
}
